import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, throwError } from 'rxjs';

import { SessionExpirationService } from '../services/session-expiration.service';

/**
 * Global HTTP interceptor that watches for 401 Unauthorized responses.
 *
 * When a 401 comes back from the backend, it means the user's session has
 * expired (or the token is otherwise invalid). The interceptor delegates
 * to SessionExpirationService, which dismisses open modals, clears
 * localStorage, signs out of Firebase, and navigates to /login.
 *
 * The auth.guard does the same thing but only on a fresh navigation, so
 * any in-flight request that comes back with 401 (e.g. user idle on the
 * ECO wizard for 8 hours, the wizard fires a request, the request 401s)
 * goes through THIS interceptor. SessionExpirationService is the single
 * source of truth for the teardown sequence.
 *
 * Implementation notes:
 *   - We whitelist the `/login` and `/login/renew` URLs to avoid an
 *     infinite loop on the login page itself (the renew call would 401
 *     and re-trigger the redirect).
 *   - We do NOT swallow the 401 — the calling component still receives
 *     the error so it can decide whether to surface a toast, hide a
 *     spinner, etc.
 */
@Injectable()
export class AuthExpiredInterceptor implements HttpInterceptor {
  private readonly sessionExpirationService = inject(SessionExpirationService);

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status === 401 && !this.isWhitelisted(req.url)) {
          this.sessionExpirationService.handleSessionExpired();
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Auth-related endpoints must not trigger the redirect on 401, otherwise
   * the login form itself would bounce back to /login.
   */
  private isWhitelisted(url: string): boolean {
    if (!url) return true;
    return url.includes('/login') || url.includes('/login/renew') || url.includes('/login/google');
  }
}